import { cn } from '@syncanix/ui';

import type { PropsWithChildren, ReactElement } from 'react';

export interface TopBarProps extends PropsWithChildren {
  /**
   * Left-edge slot, typically the workspace switcher. Inheriting the same
   * shape as `children` keeps the API ergonomic for partial-slot callers.
   */
  start?: ReactElement;
  /** Centre slot — the settings search on desktop. */
  center?: ReactElement;
  /** Right-edge slot — env switcher + user menu. */
  end?: ReactElement;
  className?: string;
}

/**
 * Compact top-of-page banner. Vercel-style: thin, dense, sticky-when-needed
 * (the AppShell decides). Slots make the bar reusable across surfaces
 * (settings, onboarding) without rebuilding the layout.
 */
export function TopBar({ start, center, end, className, children }: TopBarProps): ReactElement {
  return (
    <header
      role="banner"
      className={cn(
        // Tighter gap/padding on narrow screens so the start + end controls fit a
        // 320–375px bar without horizontal overflow; full spacing returns at sm.
        // Bedrock §08: a 56px `surface` bar closed by a logical hairline. It is
        // RULED against the canvas beneath it, never shadowed — principle 01,
        // and the reason the shell reads as one sheet rather than as a floating
        // bar over a page.
        'flex h-topbar shrink-0 items-center gap-2 border-b border-hairline bg-surface px-3 sm:gap-3 sm:px-3.5',
        className,
      )}
    >
      {/* `min-w-0` lets the start slot (workspace switcher) shrink + truncate
          instead of forcing the bar wider than the viewport. */}
      {start ? <div className="flex min-w-0 items-center gap-2 sm:gap-3">{start}</div> : null}
      {/* `min-w-0` for the same reason the start slot has it: without it a
          flex child refuses to shrink below its content, so the search field's
          own minimum width pushes the bar past the viewport instead of the
          field narrowing. Invisible in English at 1280px, and a horizontally
          scrolling page in German at 768. */}
      <div className="flex min-w-0 flex-1 items-center justify-center">{center ?? children}</div>
      {end ? <div className="flex shrink-0 items-center gap-2 sm:gap-3">{end}</div> : null}
    </header>
  );
}
